import { Address } from 'components/Models/address'
import { ImageDimensionsInput } from 'components/Models/imageDimensionsInput'

export const stringIsNullOrWhitespace = (
    input: string | null | undefined
): boolean => !input || !input.trim()

export const getCharAt = (
    input: string | undefined,
    position: number
): string => (input === undefined ? '' : input.charAt(position))

export const stripHtmlTags = (text: string): string => {
    const doc = new DOMParser().parseFromString(text, 'text/html')
    return doc.body.textContent || ''
}

export const getNameInitials = (
    firstName: string | undefined,
    lastName: string | undefined
): string => {
    const initials = `${getCharAt(firstName, 0)}${getCharAt(lastName, 0)}`
    return initials ? initials : 'UN'
}

export const getUserName = (
    firstName: string | undefined,
    lastName: string | undefined
): string => {
    if (!firstName && !lastName) {
        return 'Unknown'
    }

    return `${firstName || ''} ${lastName || ''}`
}

export const joinArray = (
    arr: (string | undefined)[],
    separator: string
): string => {
    return arr.filter(Boolean).join(separator)
}

/**
 * https://stackoverflow.com/a/46181/1417507
 * @param email string
 */
export const validateEmail = (email: string) => {
    const regx = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return regx.test(email.toLowerCase())
}

export const compareStringValues = (a: string, b: string) =>
    b.toLowerCase().localeCompare(a.toLowerCase())

export interface SuffixDimensionProps {
    /** filename or path to suffix the dimension */
    filename: string
    dimension: Partial<ImageDimensionsInput>
}

/**
 * Returns a new string with a suffixed dimension
 */
export const suffixDimension = ({
    filename,
    dimension,
}: SuffixDimensionProps) => {
    const periodIdx = filename.lastIndexOf('.')
    if (dimension.percentage)
        return `${filename.substr(0, periodIdx)}_${
            dimension.percentage
        }${filename.substr(periodIdx)}`

    return `${filename.substr(0, periodIdx)}_${dimension.width}x${
        dimension.height
    }${filename.substr(periodIdx)}`
}

/**
 * Returns a single line address string
 * @param address Address
 */
export const getFullAddress = (address: Partial<Address>) => {
    const street: (string | undefined)[] = [
        address.street1,
        address.street2,
    ].filter((value) => !!value && value !== 'N/A')

    const fullAddress: (string | undefined)[] = [
        street.join(' '),
        address.city,
        address.region,
        address.country,
    ].filter(Boolean)

    const fullZipAddress: (string | undefined)[] = [
        fullAddress.join(', '),
        address.postalCode,
    ].filter(Boolean)

    return fullZipAddress.join(' ')
}

export const capitalizeString = (value: string) =>
    `${getCharAt(value, 0).toLocaleUpperCase()}${value
        .slice(1)
        .toLocaleLowerCase()}`

/**
 * Converts string to title case.
 *
 * @param str - source string
 * @param lower - if true, sets the rest of the characters to lower string except for the first letter of each word
 */
export const makeTitleCase = (str: string, lower: boolean = false) =>
    (lower ? str.toLowerCase() : str).replace(
        /(?:^|\s|["'([{])+\S/g,
        (match: string) => match.toUpperCase()
    )

/**
 * This is a sorter function to be used with
 * String.sort(). It sorts strings alphabetically
 * in ascending order, disregarding case.
 */
export const alphabeticalStringSorter = (text1: string, text2: string) => {
    text1 = text1.toLowerCase()
    text2 = text2.toLowerCase()
    if (text1 > text2) {
        return 1
    } else if (text1 < text2) {
        return -1
    }
    return 0
}

/**
 * Convert date string with format mm/dd/yyyy to yyyy-dd-mm
 */
export const convertDateStringToISOString = (dateString: string) => {
    const dateStringArr = dateString.split('/')
    if (dateStringArr.length === 3) {
        dateStringArr.splice(0, 0, dateStringArr.pop()!)
    }
    return dateStringArr.join('-')
}

/**
 * Decode escaped characters from sanitized contenteditable fields
 */
export const decodeEntities = (encodedString: string) => {
    var translate_re = /&(nbsp|amp|quot|lt|gt);/g
    var translate: Record<string, string> = {
        nbsp: ' ',
        amp: '&',
        quot: '"',
        lt: '<',
        gt: '>',
    }
    return encodedString
        .replace(translate_re, function (_, entity) {
            return translate[entity]
        })
        .replace(/&#(\d+);/gi, function (_, numStr) {
            var num = parseInt(numStr, 10)
            return String.fromCharCode(num)
        })
}

/**
 * Encode HTML characters from string
 */
export const encodeEntities = (dirtyString: string) => {
    const el = document.createElement('div')
    el.innerText = el.textContent = dirtyString
    return el.innerHTML
}

/**
 * Pluralize an English noun (assumes it's not in all caps)
 */
export const pluralize = (str: string) => {
    if (!str) {
        return str
    }

    const strLower = str.toLowerCase()
    return strLower.endsWith('s')
        ? str + 'es'
        : strLower.endsWith('y')
        ? strLower.substring(0, strLower.length - 1) + 'ies'
        : str + 's'
}

export const truncateString = (value: string, characterLimit: number = 0) => {
    return characterLimit > 0 && value?.trim().length > characterLimit
        ? `${value.substring(0, characterLimit)}...`
        : value
}
